/**
 * @fileoverview Generic, defensive loader for a task's detail. Fetches when the
 * drawer opens, cancels stale responses (anti-race) and exposes a reload token.
 * Shared by the deliverable/self/final drawer hooks (no white screens).
 */

import { useCallback, useEffect, useState } from 'react'

interface UseTaskDetailResult<T> {
  loading: boolean
  errored: boolean
  data: T | null
  reload: () => void
}

export function useTaskDetail<T>(
  fetcher: (identifierId: string) => Promise<T>,
  identifierId: string,
  opened: boolean,
): UseTaskDetailResult<T> {
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [data, setData] = useState<T | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const reload = useCallback(() => setReloadToken(token => token + 1), [])

  useEffect(() => {
    if (!opened || !identifierId) return

    let cancelled = false
    setLoading(true)
    setErrored(false)
    setData(null)

    fetcher(identifierId)
      .then(result => {
        if (!cancelled) setData(result ?? null)
      })
      .catch(error => {
        console.error('Error loading task detail:', error)
        if (!cancelled) {
          setErrored(true)
          setData(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fetcher, identifierId, opened, reloadToken])

  return { loading, errored, data, reload }
}
